import {
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { internalWalletsDepositTransactionsApi } from 'api';
import { ConfirmButton, Dialog, DialogProps } from 'components';
import {
  AmlCheckTransactionStatus,
  QueryKey,
  TronTransactionStatus,
} from 'enums';
import { useMutation } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { InternalPlatformWalletDepositTransaction } from 'types';

const TRON_ADDRESS_REGEX = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;

type Props = DialogProps<InternalPlatformWalletDepositTransaction>;

export const DepositTransactionDetailsModal: React.FC<Props> = ({
  open,
  data,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.internal_wallets_transactions.transaction_details_modal',
  });
  const [useTrxForBandwidth, setUseTrxForBandwidth] = useState(false);
  const [useTrxForEnergy, setUseTrxForEnergy] = useState(false);
  const [returnAddress, setReturnAddress] = useState('');

  const isAmlFailed = useMemo(
    () =>
      data?.status === TronTransactionStatus.Error &&
      !!data?.internalWalletDepositTransactions?.length &&
      data.internalWalletDepositTransactions.every(
        (transaction) =>
          transaction.amlCheckStatus === AmlCheckTransactionStatus.Failed,
      ),
    [data],
  );
  const isReturnAddressValid = TRON_ADDRESS_REGEX.test(returnAddress.trim());

  const { mutate: statusToWaiting } = useMutation(
    internalWalletsDepositTransactionsApi.statusToWaiting,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          QueryKey.InternalWalletTransactionsDepositActive,
        );
        onClose({ ok: true });
      },
      notifierType: 'execute',
    },
  );
  const { mutate: execute } = useMutation(
    internalWalletsDepositTransactionsApi.execute,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          QueryKey.InternalWalletTransactionsDepositActive,
        );
        onClose({ ok: true });
      },
      notifierType: 'execute',
    },
  );
  const { mutate: returnFunds } = useMutation(
    internalWalletsDepositTransactionsApi.returnFunds,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          QueryKey.InternalWalletTransactionsDepositActive,
        );
        onClose({ ok: true });
      },
      notifierType: 'execute',
    },
  );

  const onConfirmToWaiting = useCallback(() => {
    statusToWaiting(data?.id!);
  }, [data?.id, statusToWaiting]);

  const onConfirmExecute = useCallback(() => {
    execute({ id: data?.id!, data: { useTrxForBandwidth, useTrxForEnergy } });
  }, [data?.id, execute, useTrxForBandwidth, useTrxForEnergy]);

  const onConfirmReturn = useCallback(() => {
    returnFunds({ id: data?.id!, returnAddress: returnAddress.trim() });
  }, [data?.id, returnFunds, returnAddress]);

  return (
    <Dialog open={open} title={t('title')} modal onClose={onClose}>
      {data?.status === TronTransactionStatus.Error && (
        <div className="tw-mb-6">
          <ConfirmButton variant="outlined" onConfirm={onConfirmToWaiting}>
            {t('to_waiting')}
          </ConfirmButton>
          <Typography variant="caption" component="div" color="textSecondary">
            {t('execute_by_schedule')}
          </Typography>
        </div>
      )}
      <ConfirmButton variant="outlined" onConfirm={onConfirmExecute}>
        {t('execute')}
      </ConfirmButton>
      <FormControlLabel
        control={
          <Checkbox
            checked={useTrxForBandwidth}
            onChange={() => setUseTrxForBandwidth((prev) => !prev)}
          />
        }
        label={t('use_trx_for_bandwidth')}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={useTrxForEnergy}
            onChange={() => setUseTrxForEnergy((prev) => !prev)}
          />
        }
        label={t('use_trx_for_energy')}
      />
      {isAmlFailed && (
        <div className="tw-mt-6">
          <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
            {t('return_title')}
          </Typography>
          <Typography
            variant="caption"
            component="div"
            color="textSecondary"
            sx={{ mb: 2.5 }}
          >
            {t('return_description')}
          </Typography>
          <TextField
            fullWidth
            size="small"
            label={t('return_address')}
            value={returnAddress}
            onChange={(event) => setReturnAddress(event.target.value)}
            error={!!returnAddress && !isReturnAddressValid}
            helperText={
              !!returnAddress && !isReturnAddressValid
                ? t('return_address_invalid')
                : undefined
            }
            sx={{ mb: 2 }}
          />
          <ConfirmButton
            variant="outlined"
            color="error"
            disabled={!isReturnAddressValid}
            onConfirm={onConfirmReturn}
          >
            {t('return')}
          </ConfirmButton>
        </div>
      )}
    </Dialog>
  );
};
